import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Bell, CheckCheck, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const PAGE_SIZE_OPTIONS = ['5', '10', '20', '50', '100', 'all'];
const ALL_FETCH_CHUNK = 200;

export default function NotificationsPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [pageSize, setPageSize] = useState('20');
  const [count, setCount] = useState({ unread_count: 0, total_count: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const totalItems = unreadOnly ? count.unread_count : count.total_count;
  const pageSizeValue = pageSize === 'all' ? Math.max(totalItems, 1) : Number(pageSize);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalItems / pageSizeValue));

  const loadNotifications = useCallback(async (targetPage = page, unreadFilter = unreadOnly, targetPageSize = pageSize) => {
    setLoading(true);
    try {
      const countRes = await api.getNotificationCount();
      const latestCount = countRes.data || { unread_count: 0, total_count: 0 };
      setCount(latestCount);

      const latestTotalItems = unreadFilter ? (latestCount.unread_count || 0) : (latestCount.total_count || 0);

      if (targetPageSize === 'all') {
        let offset = 0;
        let allItems = [];

        while (allItems.length < latestTotalItems) {
          const notifRes = await api.getNotifications(ALL_FETCH_CHUNK, unreadFilter, offset);
          const items = notifRes.data || [];
          allItems = [...allItems, ...items];
          offset += ALL_FETCH_CHUNK;
          if (items.length === 0) {
            break;
          }
        }

        setNotifications(allItems);
        setPage(1);
        return;
      }

      const numericPageSize = Number(targetPageSize);
      const offset = (targetPage - 1) * numericPageSize;
      const notifRes = await api.getNotifications(numericPageSize, unreadFilter, offset);
      setNotifications(notifRes.data || []);

      const latestTotalPages = Math.max(1, Math.ceil(latestTotalItems / numericPageSize));
      if (targetPage > latestTotalPages) {
        setPage(latestTotalPages);
      }
    } catch (error) {
      toast.error(t('notifications.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, unreadOnly, pageSize, t]);

  useEffect(() => {
    loadNotifications(page, unreadOnly, pageSize);
  }, [page, unreadOnly, pageSize, loadNotifications]);

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  const formatNotificationTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.now');
    if (diffMins < 60) return t('notifications.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('notifications.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('notifications.daysAgo', { count: diffDays });
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const handleMarkAllRead = async () => {
    setActionLoading(true);
    try {
      await api.markAllNotificationsRead();
      await loadNotifications(page, unreadOnly);
      toast.success(t('notifications.markAllSuccess'));
    } catch (error) {
      toast.error(t('notifications.actionError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await api.markNotificationRead(notificationId);
      setNotifications((previous) => previous.map((item) => (item.id === notificationId ? { ...item, read: true } : item)));
      setCount((previous) => ({
        ...previous,
        unread_count: Math.max(0, (previous.unread_count || 0) - 1),
      }));
    } catch (error) {
      toast.error(t('notifications.actionError'));
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await api.deleteNotification(notificationId);
      const deletedNotification = notifications.find((item) => item.id === notificationId);

      setNotifications((previous) => previous.filter((item) => item.id !== notificationId));
      setCount((previous) => ({
        total_count: Math.max(0, (previous.total_count || 0) - 1),
        unread_count: deletedNotification?.read ? (previous.unread_count || 0) : Math.max(0, (previous.unread_count || 0) - 1),
      }));
    } catch (error) {
      toast.error(t('notifications.actionError'));
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await handleMarkRead(notification.id);
    }
    if (notification.tontine_id) {
      navigate(`/tontines/${notification.tontine_id}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-50">
            {t('notifications.title')}
          </h1>
          <p className="text-stone-500 mt-1">{t('notifications.subtitle')}</p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={unreadOnly ? 'default' : 'outline'}
            onClick={() => {
              setPage(1);
              setUnreadOnly((previous) => !previous);
            }}
            data-testid="notifications-filter-btn"
          >
            {unreadOnly ? t('notifications.showAll') : t('notifications.showUnread')}
          </Button>
          <Button
            variant="outline"
            onClick={handleMarkAllRead}
            disabled={actionLoading || count.unread_count === 0}
            data-testid="notifications-mark-all-btn"
          >
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCheck className="w-4 h-4 mr-2" />}
            {t('notifications.markAllRead')}
          </Button>
        </div>
      </div>

      <Card className="card-neo">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>{t('notifications.title')}</span>
            <Badge variant="secondary" data-testid="notifications-count-badge">{totalItems}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-7 h-7 animate-spin text-orange-600" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-stone-500" data-testid="notifications-empty">
              <Bell className="w-10 h-10 mb-2 opacity-30" />
              <p className="text-sm">{t('notifications.noNotifications')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`rounded-xl border p-4 transition-colors cursor-pointer ${
                    !notification.read
                      ? 'border-orange-200 bg-orange-50/50 dark:border-orange-800 dark:bg-orange-900/10'
                      : 'border-stone-200 bg-stone-50 dark:border-stone-800 dark:bg-stone-900/30'
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                  data-testid={`notifications-item-${notification.id}`}
                >
                  <div className="flex gap-3">
                    <span className="text-xl flex-shrink-0">{notification.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm text-stone-900 dark:text-stone-100 ${!notification.read ? 'font-semibold' : ''}`}>
                          {notification.title}
                        </p>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-stone-400 hover:text-red-500"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDelete(notification.id);
                          }}
                          data-testid={`notifications-delete-${notification.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">{notification.message}</p>
                      <p className="text-xs text-stone-400 mt-2">{formatNotificationTime(notification.created_at)}</p>
                    </div>
                    {!notification.read && <span className="w-2 h-2 bg-orange-500 rounded-full mt-2" />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="notifications-pagination">
        <p className="text-sm text-stone-500">
          {t('common.page')} {page} / {totalPages}
        </p>
        <div className="flex items-center gap-2">
          <Select value={pageSize} onValueChange={setPageSize}>
            <SelectTrigger className="w-[120px] h-8" data-testid="notifications-page-size-select">
              <SelectValue placeholder={t('common.pageSize')} />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {option === 'all' ? t('common.all') : option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading || pageSize === 'all'}
            onClick={() => setPage((previous) => Math.max(1, previous - 1))}
            data-testid="notifications-prev-page"
          >
            {t('common.previous')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading || pageSize === 'all'}
            onClick={() => setPage((previous) => previous + 1)}
            data-testid="notifications-next-page"
          >
            {t('common.next')}
          </Button>
        </div>
      </div>
    </div>
  );
}
